import { useQuery, useQueryClient } from '@tanstack/react-query';
import { t } from 'i18next';
import { useCallback, useMemo } from 'react';
import { getCurrentPrincipal } from '../../lib/currentPrincipal';
import { principalServiceRequest } from '../../utils/principal-service/base-request';
import {
  APIKeyData,
  APIKeyLastAction,
  APIKeyStatus,
  Role,
} from '../../utils/types';
import { useCurrentUser } from './useCurrentUser';
import { usePrincipals } from './usePrincipals';
import { useUsers } from './useUsers';

type arrayofSubscriptionHistoryRequestsData = {
  data: {
    history: getSubscriptionHistoryResponseData[];
  };
};

export type getSubscriptionHistoryResponseData = {
  activity: string;
  apiKey: string;
  dateOfActivity: string;
  keyId: string;
  projectName: string;
  scopes: APIScope[];
  userName?: string;
  fromUserName?: string;
  fromUserEmail?: string;
  toUserName?: string;
  toUserEmail?: string;
  toUser?: string;
  invitedby?: string;
};

export type APIScope = {
  active: boolean;
  createdAt: string;
  description: string;
  id: string;
  name: string;
  updatedAt: string;
};

export type APISpecsResponseData = {
  id: string;
  type: string;
  name: string;
  properties: {
    format: string;
    value: {
      link: string;
    };
  };
};

export type InitialAPIData = {
  createdDate: string;
  expirationDate: string;
  id: string;
  name: string;
  notificationDate?: string;
  primaryKey: string;
  principalId: string;
  scopes: APIScope[];
  startDate: string;
  state: APIKeyStatus;
  stateComment?: string;
  lastAction: APIKeyLastAction;
  user: {
    email: string;
    firebaseId: string;
    guid: string;
  };
};

export type InitialAPIHistoryData = {
  createdDate: string;
  displayName?: string;
  id: string;
  name: string;
  primaryKey: string;
  scopes: APIScope[];
  type: string;
  user: {
    email: string;
    firebaseId: string;
    guid: string;
  };
  fromUser?: {
    email: string;
    firebaseId: string;
    guid: string;
  };
  toUser?: {
    email: string;
    firebaseId: string;
    guid: string;
  };
  invitedBy?: {
    email: string;
    firebaseId: string;
    guid: string;
  };
};

export type APIHistory = {
  activity: string;
  apiKey: string;
  dateOfActivity: string;
  keyId: string;
  projectName: string;
  scopes: APIScope[] | string[];
  userName?: string;
  fromUserName?: string;
  toUserName?: string;
  invitedby?: string;
};

export type ApiUser = {
  firebaseId: string;
  email: string;
};

export type APIsHookResult = {
  creatorId: string;
  projectName: string;
};

export type UseAPIKeyResult = {
  apiKeys: APIKeyData[] | undefined;
  areAvailableKeysLoading: boolean;
  apiHistory?: APIHistory[];
  isApiKeyHistoryLoading: boolean;
  apiHistoryWithAdditions?: APIHistory[];
  isApiKeyHistoryWithAdditionsLoading: boolean;
  apiSpecification: APISpecsResponseData | undefined;
  isApiSpecificationLoading: boolean;
  createSubscription: (
    partial: Partial<APIKeyData>,
  ) => Promise<InitialAPIHistoryData>;
  deleteSubscription: (selectedApiId: string) => Promise<void>;
  moveSubscription: (
    selectedApiId: string,
    userIdFrom: string,
    userIdTo: string,
  ) => Promise<void>;
};

export const useApiKeys = (): UseAPIKeyResult => {
  const queryClient = useQueryClient();
  const { user: currentUser, role } = useCurrentUser();
  const { users } = useUsers({
    disabled: role === Role.PRINCIPAL_DEVELOPER,
  });
  const { data: principals } = usePrincipals();
  const { currentPrincipal } = getCurrentPrincipal(principals);

  const { data: availableKeys, isLoading: areAvailableKeysLoading } = useQuery({
    queryFn: async () => {
      return await principalServiceRequest<{ subscriptions: APIKeyData[] }>(
        'get',
        `subscriptions/${currentPrincipal.id}`,
      );
    },
    queryKey: [
      'subscriptions',
      currentPrincipal.id,
      users?.map(({ user }) => user.userId),
    ],
    select: ({ data }) => data.subscriptions,
    refetchOnWindowFocus: false,
  });

  const { data: apiHistory, isLoading: isApiKeyHistoryLoading } = useQuery({
    queryFn: async () => {
      const response = await principalServiceRequest<{
        history: getSubscriptionHistoryResponseData;
      }>('get', `subscriptions/${currentPrincipal.id}/history`);
      return response.data.history;
    },
    queryKey: ['history', currentPrincipal.id],
    select: (data) => (Array.isArray(data) ? data : undefined),
    refetchOnWindowFocus: false,
  });

  const {
    data: apiHistoryWithAdditions,
    isLoading: isApiKeyHistoryWithAdditionsLoading,
  } = useQuery({
    queryFn: async () => {
      if (!availableKeys) {
        return;
      }
      let arrayofSubscriptionHistory: arrayofSubscriptionHistoryRequestsData[];
      try {
        arrayofSubscriptionHistory = await Promise.all(
          availableKeys.map(({ keyId }) => {
            return principalServiceRequest<{
              history: getSubscriptionHistoryResponseData[];
            }>('get', `subscriptions/${currentPrincipal.id}/history/${keyId}`);
          }),
        );
      } catch (error) {
        console.log('arrayofSubscriptionHistory error: ', error);
        return undefined;
      }

      const arrayofSubscriptionHistoryWithAddition = arrayofSubscriptionHistory
        .flatMap((response) => response.data.history)
        .map((historyItem) => {
          let overwrittenValues = {};
          const overwrittenValuesTranslationKey =
            'features:collaboration:api-management:keyHistory';
          if (historyItem.fromUserEmail === currentUser.email) {
            overwrittenValues = {
              userName: t(`${overwrittenValuesTranslationKey}:youCaps`),
              fromUserName: t(`${overwrittenValuesTranslationKey}:youCaps`),
              toUser: historyItem.toUserEmail,
            };
          }

          if (historyItem.userName === currentUser.name) {
            overwrittenValues = {
              userName: t(`${overwrittenValuesTranslationKey}:youCaps`),
              toUser: historyItem.toUserEmail,
            };
          }

          if (historyItem.toUserEmail === currentUser.email) {
            overwrittenValues = {
              userName: historyItem.fromUserEmail,
              toUser: t(`${overwrittenValuesTranslationKey}:youSmall`),
              toUserName: t(`${overwrittenValuesTranslationKey}:youSmall`),
            };
          }

          return { ...historyItem, ...overwrittenValues };
        });

      return arrayofSubscriptionHistoryWithAddition;
    },
    queryKey: ['history', currentPrincipal.id, users, availableKeys],
    select: (data) => (Array.isArray(data) ? data : undefined),
    refetchOnWindowFocus: false,
  });

  const createSubscription = useCallback(
    async (newApi: Partial<APIKeyData>): Promise<InitialAPIHistoryData> => {
      const response = await principalServiceRequest<InitialAPIHistoryData>(
        'Post',
        `subscriptions`,
        {
          email: currentUser.email,
          principalId: currentPrincipal.id,
          projectName: newApi.projectName,
          scopes: newApi.scopes,
        },
      );
      queryClient.invalidateQueries({
        queryKey: ['subscriptions'],
      });

      return response.data;
    },
    [currentPrincipal.id, currentUser.email, queryClient],
  );

  const deleteSubscription = useCallback(
    async (selectedApiId: string): Promise<void> => {
      await principalServiceRequest(
        'Delete',
        `subscriptions/${currentPrincipal.id}/${selectedApiId}`,
        {},
      );
      queryClient.invalidateQueries({
        queryKey: ['subscriptions'],
      });
      queryClient.invalidateQueries({
        queryKey: ['history'],
      });
    },
    [currentPrincipal.id, queryClient],
  );

  const moveSubscription = useCallback(
    async (
      selectedApiId: string,
      userIdFrom: string,
      userIdTo: string,
    ): Promise<void> => {
      const response = await principalServiceRequest(
        'Put',
        `subscriptions/${currentPrincipal.id}/${selectedApiId}`,
        {
          userIdFrom,
          userIdTo,
        },
      );
      if (response) {
        queryClient.invalidateQueries({
          queryKey: ['subscriptions', 'history'],
        });
      }
    },
    [currentPrincipal.id, queryClient],
  );

  const { data: apiSpecification, isLoading: isApiSpecificationLoading } =
    useQuery({
      queryFn: async () => {
        return await principalServiceRequest<APISpecsResponseData>(
          'Get',
          `specification`,
        );
      },
      queryKey: ['apiSpecification'],
      select: ({ data }) => data,
      refetchOnWindowFocus: false,
    });

  const apiData = useMemo(() => {
    return {
      availableKeys,
      areAvailableKeysLoading,
      apiSpecification,
      isApiSpecificationLoading,
      apiHistory,
      isApiKeyHistoryLoading,
      apiHistoryWithAdditions,
      isApiKeyHistoryWithAdditionsLoading,
    };
  }, [
    availableKeys,
    areAvailableKeysLoading,
    apiSpecification,
    isApiSpecificationLoading,
    apiHistory,
    isApiKeyHistoryLoading,
    apiHistoryWithAdditions,
    isApiKeyHistoryWithAdditionsLoading,
  ]);

  return {
    areAvailableKeysLoading: apiData.areAvailableKeysLoading,
    apiKeys: apiData.availableKeys,
    apiSpecification: apiData.apiSpecification,
    isApiSpecificationLoading: apiData.isApiSpecificationLoading,
    apiHistory: apiData.apiHistory,
    isApiKeyHistoryLoading: apiData.isApiKeyHistoryLoading,
    apiHistoryWithAdditions: apiData.apiHistoryWithAdditions,
    isApiKeyHistoryWithAdditionsLoading:
      apiData.isApiKeyHistoryWithAdditionsLoading,
    createSubscription,
    deleteSubscription,
    moveSubscription,
  };
};
